import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ExternalLink, Pencil, Plus, Repeat, Settings } from 'lucide-react';
import {
  useAction,
  useWatchableValue,
} from '@cotera/client/v0/actions/framework';
import { useArtifactPanel } from '@cotera/client/v0/chat/artifact-panel-context';
import { Text } from '@cotera/client/app/components/ui';
import { Button } from '@cotera/client/v0/components/ui/button';
import { Input } from '@cotera/client/v0/components/ui/input';
import { Textarea } from '@cotera/client/v0/components/ui/textarea';
import { useToolCallUpstreamSamples } from '../../../hooks/use-tool-call-upstream-samples';
import { useOpenAddWorkflowNodeModal } from '../../../use-open-add-workflow-node-modal';
import { useWorkflowGraphMutations } from '../../../use-workflow-graph-mutations';
import { useWorkflowModel } from '../../../workflow.model';
import { useAgentOptions } from '@cotera/client/app/v1/trigger/hooks';
import { useOpenWorkflowAgent } from '../../../use-open-workflow-resource';
import { NewAgentAction } from '../../../../agent/new-agent.action';
import { NewWorkflowAction } from '../../../new-workflow.action';
import { asRecord, readString } from '../../utils';
import {
  isConfigurableForEachBodyType,
  isForEachBodyType,
  listForEachSuccessorOptions,
  partitionForEachSuccessors,
} from './for-each-child-node';
import {
  forEachDataFromUpstreamRef,
  findUpstreamFieldForForEach,
  preferArrayUpstreamSources,
} from './for-each-items-path';
import { buildUpstreamSources } from './variable-picker-data';
import {
  UpstreamRefChip,
  UpstreamRefPicker,
  UpstreamRefPickerButton,
} from './upstream-ref-picker';
import { useWorkflowOpenNodeConfig } from '../../workflow-open-node-config-context';
import type { WorkflowNodeEditorProps } from './types';
import type { WorkflowNodePaletteSelection } from '../../workflow-node-palette';

/** Reads a stored literal `items` list as display strings (or undefined). */
function readItemsList(
  record: Record<string, unknown> | null
): string[] | undefined {
  const value = record === null ? undefined : record['items'];
  if (!Array.isArray(value)) {
    return undefined;
  }
  return value.map((item) =>
    typeof item === 'string' ? item : JSON.stringify(item)
  );
}

/** Splits typed text into list items on newlines or commas, dropping blanks. */
function parseFixedItems(text: string): string[] {
  return text
    .split(/[\n,]/)
    .map((entry) => entry.trim())
    .filter((entry) => entry !== '');
}

/**
 * Config editor for a for-each node: choose the list to fan out over — either a
 * fixed list you type or an array from a previous step — and which step runs as
 * the loop body (same add-node picker as the canvas). Any other node wired from
 * this for-each is a post-barrier fan-in continuation (runs once after all
 * iterations complete).
 */
export const ForEachNodeEditor: React.FC<WorkflowNodeEditorProps> = ({
  node,
  onChange,
}) => {
  const model = useWorkflowModel();
  const graph = useWatchableValue(model.graphWatchable);
  const mutations = useWorkflowGraphMutations();
  // Opens the draft agent as a sibling artifact tab instead of navigating the
  // whole app away from the workflow being edited. Outside a panel there is no
  // beside, and the action navigates as usual.
  const artifactPanel = useArtifactPanel();
  const openWorkflowAgent = useOpenWorkflowAgent();
  const { execute: createAgent } = useAction(NewAgentAction);
  const openNewAgentArtifact = useCallback(() => {
    void createAgent({
      newChat: false,
      ...(artifactPanel !== null && {
        openArtifact: artifactPanel.openArtifact,
      }),
    });
  }, [artifactPanel, createAgent]);
  // Same beside-not-away treatment for a brand-new workflow to loop over.
  const { execute: createWorkflow } = useAction(NewWorkflowAction);
  const openNewWorkflowArtifact = useCallback(() => {
    void createWorkflow({
      newChat: false,
      ...(artifactPanel !== null && {
        openArtifact: artifactPanel.openArtifact,
      }),
    });
  }, [artifactPanel, createWorkflow]);
  const data = asRecord(node.data);
  const [itemsPath, setItemsPath] = useState<string | undefined>(
    readString(data, 'itemsPath')
  );
  const [sourceRunNodeId, setSourceRunNodeId] = useState<string | undefined>(
    readString(data, 'sourceRunNodeId')
  );
  const initialItems = readItemsList(data);
  const [itemsMode, setItemsMode] = useState<'upstream' | 'fixed'>(
    initialItems !== undefined ? 'fixed' : 'upstream'
  );
  const [itemsText, setItemsText] = useState<string>(
    initialItems !== undefined ? initialItems.join('\n') : ''
  );

  const [seededNodeId, setSeededNodeId] = useState(node.id);
  if (seededNodeId !== node.id) {
    setSeededNodeId(node.id);
    const next = asRecord(node.data);
    setItemsPath(readString(next, 'itemsPath'));
    setSourceRunNodeId(readString(next, 'sourceRunNodeId'));
    const nextItems = readItemsList(next);
    setItemsMode(nextItems !== undefined ? 'fixed' : 'upstream');
    setItemsText(nextItems !== undefined ? nextItems.join('\n') : '');
  }

  const successors = useMemo(
    () =>
      graph === null
        ? []
        : listForEachSuccessorOptions({
            nodes: graph.nodes,
            edges: graph.edges,
            forEachNodeId: node.id,
          }),
    [graph, node.id]
  );

  const graphChildNodeId = useMemo(() => {
    if (graph === null) {
      return readString(data, 'childNodeId');
    }
    const forEach = graph.nodes.find((row) => row.id === node.id);
    return readString(asRecord(forEach?.data), 'childNodeId');
  }, [graph, node.id, data]);

  const { bodyId, fanInIds } = useMemo(
    () =>
      partitionForEachSuccessors({
        outgoingIds: successors.map((option) => option.id),
        childNodeId: graphChildNodeId,
      }),
    [successors, graphChildNodeId]
  );

  const bodyOption = useMemo(
    () => successors.find((option) => option.id === bodyId),
    [successors, bodyId]
  );

  // The body node's type, read from the live graph — decides whether it has a
  // config form we can drill into (a tool call, filter or sub-workflow) vs. an
  // agent (which opens its own resource instead).
  const bodyNodeType = useMemo(() => {
    if (graph === null || bodyId === undefined) {
      return undefined;
    }
    return graph.nodes.find((row) => row.id === bodyId)?.type;
  }, [graph, bodyId]);

  const openNodeConfig = useWorkflowOpenNodeConfig();
  const canConfigureBody =
    bodyId !== undefined &&
    isConfigurableForEachBodyType(bodyNodeType) &&
    openNodeConfig !== null;

  // The loop-body node is hidden from the canvas, so the only way to reach the
  // agent it runs is from here. Resolve its `agentId` (set on `agent` and, when
  // linked, `ad-hoc-agent` bodies) so we can show and open the real resource.
  const bodyAgentId = useMemo(() => {
    if (graph === null || bodyId === undefined) {
      return undefined;
    }
    const body = graph.nodes.find((row) => row.id === bodyId);
    return readString(asRecord(body?.data), 'agentId');
  }, [graph, bodyId]);

  // Resolve the linked agent live (title can drift from the snapshot stamped on
  // the node when it was picked), matching the agent node editor.
  const agents = useAgentOptions();
  const linkedAgent = useMemo(
    () =>
      bodyAgentId === undefined || bodyAgentId === ''
        ? undefined
        : agents.find((agent) => agent.id === bodyAgentId),
    [agents, bodyAgentId]
  );

  // Open the loop-body agent as a sibling artifact (or drill into the workflow's
  // own stack on the standalone page) — the same affordance as the agent node.
  const openLoopBodyAgent = useCallback((): void => {
    openWorkflowAgent(bodyAgentId, linkedAgent?.title ?? linkedAgent?.agentKey);
  }, [openWorkflowAgent, bodyAgentId, linkedAgent]);

  const fanInLabels = successors
    .filter((option) => fanInIds.includes(option.id))
    .map((option) => option.label);

  // Existing successors that could be pulled "inside" the loop as its body.
  // Only body-eligible node types qualify — the same set `addForEachBodyNode`
  // can create.
  const adoptableSuccessors = useMemo(() => {
    if (graph === null) {
      return [];
    }
    const typeById = new Map(graph.nodes.map((row) => [row.id, row.type]));
    return successors.filter((option) => {
      if (!fanInIds.includes(option.id)) {
        return false;
      }
      return isForEachBodyType(typeById.get(option.id));
    });
  }, [graph, successors, fanInIds]);

  const handleLoopBodySelect = useCallback(
    (selection: WorkflowNodePaletteSelection) => {
      void mutations.addForEachBodyNode(node.id, selection);
    },
    [mutations, node.id]
  );

  const handleAdoptBody = useCallback(
    (bodyNodeId: string) => {
      void mutations.adoptForEachBodyNode(node.id, bodyNodeId);
    },
    [mutations, node.id]
  );

  const openLoopBodyModal = useOpenAddWorkflowNodeModal(
    (selection) => handleLoopBodySelect(selection),
    node.id,
    {
      canAddTrigger: false,
      intent: 'loop-body',
      onAddTrigger: () => undefined,
      onCreateAgent: openNewAgentArtifact,
      onCreateWorkflow: openNewWorkflowArtifact,
      // A workflow looping over its own items would recurse forever.
      excludeWorkflowId: model.workflowId,
    }
  );

  const { upstreamSamples } = useToolCallUpstreamSamples(node.id);
  // A for-each can loop over an array from its direct input OR any earlier
  // node's output — the saga resolves the chosen source (by display name) from
  // the ancestor outputs, so offer every ancestor here.
  const sources = useMemo(
    () => preferArrayUpstreamSources(buildUpstreamSources(upstreamSamples)),
    [upstreamSamples]
  );
  const sourceLabels = useMemo(
    () =>
      new Map(
        upstreamSamples.map(
          (sample) => [sample.sourceName, sample.label] as const
        )
      ),
    [upstreamSamples]
  );

  const selectedField = useMemo(
    () =>
      findUpstreamFieldForForEach({
        sources,
        itemsPath,
        sourceRunNodeId,
      }),
    [sources, itemsPath, sourceRunNodeId]
  );

  useEffect(() => {
    const next: {
      items?: string[];
      itemsPath?: string;
      sourceRunNodeId?: string;
      childNodeId?: string;
      bodyType?: string;
      bodyName?: string;
    } = {};
    if (itemsMode === 'fixed') {
      // A fixed list is authoritative; don't also emit an upstream path.
      const parsed = parseFixedItems(itemsText);
      if (parsed.length > 0) {
        next.items = parsed;
      }
    } else {
      if (itemsPath !== undefined && itemsPath.trim() !== '') {
        next.itemsPath = itemsPath.trim();
      }
      if (sourceRunNodeId !== undefined && sourceRunNodeId.trim() !== '') {
        next.sourceRunNodeId = sourceRunNodeId;
      }
    }
    const effectiveChild =
      graphChildNodeId !== undefined && graphChildNodeId.trim() !== ''
        ? graphChildNodeId
        : bodyId;
    if (effectiveChild !== undefined && effectiveChild.trim() !== '') {
      next.childNodeId = effectiveChild;
    }
    const forEachData =
      graph === null
        ? data
        : asRecord(graph.nodes.find((row) => row.id === node.id)?.data);
    const bodyType = readString(forEachData, 'bodyType');
    const bodyName = readString(forEachData, 'bodyName');
    if (bodyType !== undefined && bodyType.trim() !== '') {
      next.bodyType = bodyType;
    }
    if (bodyName !== undefined && bodyName.trim() !== '') {
      next.bodyName = bodyName;
    }
    onChange({ type: 'for-each', data: next });
  }, [
    itemsMode,
    itemsText,
    itemsPath,
    sourceRunNodeId,
    graphChildNodeId,
    bodyId,
    graph,
    node.id,
    data,
    onChange,
  ]);

  const onPick = (ref: string): void => {
    const next = forEachDataFromUpstreamRef(ref);
    setItemsPath(next.itemsPath);
    setSourceRunNodeId(next.sourceRunNodeId);
  };

  const onClear = (): void => {
    setItemsPath(undefined);
    setSourceRunNodeId(undefined);
  };

  const selectedRef = selectedField?.ref;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <Text.Caption className="text-muted-foreground block">
          Items to iterate
        </Text.Caption>
        <Text.Caption className="text-muted-foreground/80 block">
          Each item becomes one loop iteration. Type your own list, or pull an
          array from a previous step.
        </Text.Caption>
        <div className="border-input bg-muted/40 flex items-center gap-1 rounded-md border p-0.5">
          <Button
            type="button"
            size="sm"
            variant={itemsMode === 'fixed' ? 'default' : 'ghost'}
            className="h-7 flex-1"
            onClick={() => setItemsMode('fixed')}
          >
            A fixed list
          </Button>
          <Button
            type="button"
            size="sm"
            variant={itemsMode === 'upstream' ? 'default' : 'ghost'}
            className="h-7 flex-1"
            onClick={() => setItemsMode('upstream')}
          >
            From a previous step
          </Button>
        </div>
        {itemsMode === 'fixed' ? (
          <div className="flex flex-col gap-1">
            <Textarea
              value={itemsText}
              rows={4}
              placeholder={
                'One item per line, or comma-separated\ne.g. first value, second value, third value'
              }
              onChange={(event) => setItemsText(event.target.value)}
            />
            <Text.Caption className="text-muted-foreground/80 block">
              {parseFixedItems(itemsText).length > 0
                ? `${
                    parseFixedItems(itemsText).length
                  } item(s) — the loop runs once per item.`
                : 'Add one item per line (or separate them with commas).'}
            </Text.Caption>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2">
              {selectedRef !== undefined ? (
                <UpstreamRefChip
                  refValue={selectedRef}
                  sourceLabels={sourceLabels}
                  onRemove={onClear}
                />
              ) : itemsPath !== undefined && itemsPath.trim() !== '' ? (
                <UpstreamRefChip
                  refValue={itemsPath}
                  sourceLabels={sourceLabels}
                  onRemove={onClear}
                />
              ) : (
                <UpstreamRefPicker sources={sources} onPick={onPick}>
                  <button
                    type="button"
                    className="border-input bg-background hover:bg-muted/60 text-muted-foreground flex h-8 flex-1 items-center justify-start rounded-md border px-3 text-left text-sm"
                  >
                    {sources.length > 0
                      ? 'Select an array field…'
                      : 'No upstream fields yet'}
                  </button>
                </UpstreamRefPicker>
              )}
              {selectedRef !== undefined ||
              (itemsPath !== undefined && itemsPath.trim() !== '') ? (
                <UpstreamRefPickerButton
                  sources={sources}
                  onPick={onPick}
                  title="Change array field"
                />
              ) : null}
            </div>
            {selectedField !== null && !Array.isArray(selectedField.value) ? (
              <Text.Caption className="text-destructive block">
                Selected field is not an array in the sample output. Fan-out
                will fail at runtime unless the live value is an array.
              </Text.Caption>
            ) : null}
            <div className="flex flex-col gap-1 pt-1">
              <Text.Caption className="text-muted-foreground/80 block">
                Or type the path manually (useful before the workflow has run,
                when no sample fields are available yet).
              </Text.Caption>
              <Input
                value={itemsPath ?? ''}
                placeholder="e.g. items or data/results"
                onChange={(event) => {
                  const value = event.target.value;
                  setItemsPath(value === '' ? undefined : value);
                  // A hand-typed path targets the sole predecessor; drop any
                  // picker-chosen source so fan-out reads from the single parent.
                  setSourceRunNodeId(undefined);
                }}
              />
            </div>
          </>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <Text.Caption className="text-muted-foreground block">
          Loop body
        </Text.Caption>
        <Text.Caption className="text-muted-foreground/80 block">
          The step that runs once per item (shown as badges on the for-each
          card). Edges from this for-each on the canvas are fan-in.
        </Text.Caption>
        {bodyOption !== undefined ? (
          <div className="flex items-center gap-2">
            <div className="border-input bg-background flex h-9 min-w-0 flex-1 items-center rounded-md border px-3 text-sm">
              <span className="truncate">
                {linkedAgent?.title ??
                  linkedAgent?.agentKey ??
                  bodyOption.label}
              </span>
            </div>
            {bodyAgentId !== undefined && bodyAgentId !== '' ? (
              <Button
                type="button"
                variant="outline"
                title="Open agent"
                aria-label="Open agent"
                className="h-9 shrink-0 gap-2"
                onClick={openLoopBodyAgent}
              >
                <ExternalLink className="h-4 w-4" />
                Open
              </Button>
            ) : null}
            {canConfigureBody ? (
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="h-9 w-9 shrink-0"
                title="Configure loop body"
                aria-label="Configure loop body"
                onClick={() => {
                  if (bodyId !== undefined) {
                    openNodeConfig?.(bodyId);
                  }
                }}
              >
                <Settings className="h-4 w-4" />
              </Button>
            ) : null}
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-9 w-9 shrink-0"
              title="Change loop body"
              onClick={() => openLoopBodyModal(node.id)}
            >
              <Pencil className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <Button
              type="button"
              variant="outline"
              className="h-9 justify-start gap-2"
              onClick={() => openLoopBodyModal(node.id)}
            >
              <Plus className="h-4 w-4" />
              Choose loop body…
            </Button>
            {adoptableSuccessors.length > 0 ? (
              <div className="flex flex-col gap-1">
                <Text.Caption className="text-muted-foreground/80 block">
                  Or move an existing step inside the loop:
                </Text.Caption>
                {adoptableSuccessors.map((option) => (
                  <Button
                    key={option.id}
                    type="button"
                    variant="outline"
                    className="h-9 justify-start gap-2"
                    onClick={() => handleAdoptBody(option.id)}
                  >
                    <Repeat className="h-4 w-4" />
                    Move “{option.label}” inside the loop
                  </Button>
                ))}
              </div>
            ) : null}
          </div>
        )}
        {fanInLabels.length > 0 ? (
          <Text.Caption className="text-muted-foreground/80 block">
            Fan-in after the loop: {fanInLabels.join(', ')}. These run once
            after every iteration finishes.
          </Text.Caption>
        ) : bodyOption !== undefined ? (
          <Text.Caption className="text-muted-foreground/80 block">
            Wire another node from this for-each on the canvas to continue after
            the loop (fan-in).
          </Text.Caption>
        ) : null}
      </div>
    </div>
  );
};
